// Named-window completion after `OVER ` / `OVER (`.
//
// The production consumer of `QueryStructure.windows`: when the cursor sits at
// an `OVER `/`OVER (` slot, we parse the current statement's `WINDOW w AS (…)`
// definitions (via the canonical `extractQueryStructure` — the SAME structural
// pass VQE and the type-check collector use) and surface the window NAMES as
// tier-1 completion candidates. Named windows exist on all five engines
// (SQL Server 2022 / Oracle 21c gained the clause); the extractor only
// populates `windows` when a real `WINDOW w AS (…)` definition parses, so a
// statement with no such clause yields an empty list here regardless of the slot.
//
// Purely additive: candidates are pushed at tier 1 so they rank ABOVE the
// generic keyword/column vocabulary the resolver still emits for the position;
// nothing is suppressed. Gated by a cheap text scan + a `\bWINDOW\b` fast-path,
// so the (heavier) structural parse only runs at the actual OVER slot in a
// statement that has a WINDOW clause.

import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '@/features/sql-grammar/document';
import type { SqlEngine } from '@/features/sql-grammar/parseOptions';
import type { CompletionCandidate } from '@/core/engines';
import type { NamedWindowDef, ParsedBuffer } from '@/features/sql-resolver/types';
import { extractQueryStructure } from '@/features/sql-resolver/structure/extractQueryStructure';

// Cursor is right after `OVER ` (a bare named-window reference, `OVER w`) …
const AFTER_OVER_BARE_RE = /\bOVER\s+([A-Za-z_$][\w$]*)?$/i;
// … or right after `OVER (` (a window-definition body whose first token may be
// a base-window name, `OVER (w …)`).
const AFTER_OVER_PAREN_RE = /\bOVER\s*\(\s*([A-Za-z_$][\w$]*)?$/i;

const QUERY_KINDS: ReadonlySet<string> = new Set(['select', 'insert', 'update', 'delete', 'merge']);

function foldingForEngine(engine: SqlEngine): 'lower' | 'upper' | 'case-insensitive' {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}

/** True when `slice` (statement start → cursor) ends at an `OVER `/`OVER (` slot. */
export function isAfterOverSlot(slice: string): boolean {
  return AFTER_OVER_BARE_RE.test(slice) || AFTER_OVER_PAREN_RE.test(slice);
}

/**
 * Named-window definitions of the given statement text — the folded-name
 * source for after-`OVER` completion. Reads `QueryStructure.windows` off the
 * canonical structural extraction; returns `[]` on any parse failure or when
 * the statement has no WINDOW clause. `statementText` should be the isolated
 * current statement (offsets are irrelevant — only names are read).
 */
export function inferNamedWindows(statementText: string, engine: SqlEngine): readonly NamedWindowDef[] {
  // Fast-path: no WINDOW keyword ⇒ no named windows. Avoids the structural
  // parse for the common `OVER (PARTITION BY …)` case with no WINDOW clause.
  if (!/\bWINDOW\b/i.test(statementText)) return [];
  const buffer = parseBuffer(statementText, engine);
  if (!buffer) return [];
  for (const stmt of buffer.documentStructure.statements) {
    if (!QUERY_KINDS.has(stmt.kind)) continue;
    try {
      const structure = extractQueryStructure(buffer, stmt);
      if (structure.windows && structure.windows.length > 0) return structure.windows;
    } catch {
      continue; // defensive — a mid-type statement must never throw here.
    }
  }
  return [];
}

/**
 * Emit named-window candidates when the cursor is at an `OVER `/`OVER (` slot
 * and the current statement declares windows. Returns true when any candidate
 * was pushed (the caller does NOT short-circuit — the window names are additive
 * and rank above the position's generic vocabulary).
 */
export function emitNamedWindowCandidates(params: {
  readonly out: CompletionCandidate[];
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly statementEnd: number;
  readonly engine: SqlEngine;
}): boolean {
  const { out, text, offset, statementStart, statementEnd, engine } = params;
  if (!isAfterOverSlot(text.slice(statementStart, offset))) return false;
  const defs = inferNamedWindows(text.slice(statementStart, statementEnd), engine);
  if (defs.length === 0) return false;
  const seen = new Set<string>();
  for (const def of defs) {
    const key = def.name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      label: def.name,
      insertText: def.name,
      isSnippet: false,
      kind: 'alias',
      tier: 1,
      detail: 'Named window',
    });
  }
  return true;
}

function parseBuffer(source: string, engine: SqlEngine): ParsedBuffer | null {
  try {
    const options = { engine, identifierFolding: foldingForEngine(engine) } as const;
    const parsedDocument = buildParsedDocumentFromSource(source, options);
    const documentStructure = getDocumentStructure(parsedDocument, 'named-windows');
    return { parsedDocument, documentStructure };
  } catch {
    return null;
  }
}
